/**
 * Stage primitives — the vocabulary every first-run screen is built
 * from, ported from the "Orbit First Run v4" prototype.
 *
 *   <StageRow>      a row of floating chips, optionally nudged sideways
 *   <StageChip>     a glass pill: label, icon or swatch, selected state
 *   <StageCapsule>  the lit capsule in the centre — a value, a field,
 *                   a toggle, a progress bar, Nova beside it
 *   <StageHint>     one quiet line under a row
 *   <StageText>     the eyebrow / title / body block at the bottom
 *   <StageCTA>      the breathing Mission-Blue button, skip, note
 *   <FirstRunFrame> the page: backdrop, orb, top bar, stage, text, CTA
 *
 * Each wraps itself in the `data-a` kind stageMotion.ts animates.
 */
import { type ReactNode, forwardRef } from 'react';
import { Nova, type NovaState } from '@/components/nova';
import { FirstRunBackdrop, StageOrb, StageSparks } from './FirstRunBackdrop';
import { MISSION_BLUE, iconFor } from './tokens';

// ── Chips ──────────────────────────────────────────────────────────

export interface StageChipProps {
  label: string;
  /** Selected: fills with the accent. */
  selected?: boolean;
  accent?: string;
  /** Colour swatch instead of an icon (craft / goal choices). */
  swatch?: string;
  /** Larger chip (craft and goal pickers). */
  big?: boolean;
  /** 50pt chip (contact / import choices). */
  tall?: boolean;
  /** Hide the tick when selected (swatch chips). */
  noCheck?: boolean;
  onClick?: () => void;
  /** Index in the stage, drives the float phase. */
  index?: number;
  apple?: boolean;
  google?: boolean;
  ink?: string;
}

export const StageChip = forwardRef<HTMLButtonElement, StageChipProps>(function StageChip(
  { label, selected, accent = '#E8B54A', swatch, big, tall, noCheck, onClick, index = 0, apple, google, ink },
  ref,
) {
  const icon = swatch || apple || google ? '' : iconFor(label);
  const float = onClick
    ? 'none'
    : `${['frFloatA', 'frFloatB', 'frFloatC'][index % 3]} ${(9 + (index % 4) * 1.6).toFixed(2)}s ease-in-out -${(index * 2.1).toFixed(1)}s infinite`;
  const h = big ? 'clamp(56px, 4.4vw, 64px)' : tall ? '50px' : '44px';
  const pad = big ? '0 clamp(22px, 2vw, 30px)' : '0 18px';
  const fs = big ? 'clamp(18px, 1.5vw, 21px)' : '15px';
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      style={{
        animation: float,
        height: h,
        padding: pad,
        fontSize: fs,
        cursor: onClick ? 'pointer' : 'default',
        background: selected ? accent : 'linear-gradient(180deg, rgba(255,255,255,.96), rgba(236,228,255,.86))',
        color: selected ? '#1A1422' : ink || '#5A2FB8',
        boxShadow: selected
          ? `0 0 0 5px ${accent}40, 0 18px 40px ${accent}55, inset 0 1px 0 rgba(255,255,255,.5)`
          : `0 18px 40px rgba(20,6,50,.4), 0 2px 6px rgba(0,0,0,.2), inset 0 1px 0 rgba(255,255,255,.95), inset 0 -1px 0 ${accent}33`,
      }}
      className="inline-flex items-center gap-[9px] whitespace-nowrap rounded-full border-0 font-medium tracking-[-0.01em] backdrop-blur-xl transition-[background,box-shadow] duration-200"
    >
      {apple && (
        <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M16.37 12.63c-.02-2.2 1.8-3.26 1.88-3.31-1.03-1.5-2.62-1.7-3.19-1.73-1.36-.14-2.65.8-3.34.8-.69 0-1.75-.78-2.88-.76-1.48.02-2.85.86-3.61 2.19-1.54 2.67-.39 6.62 1.11 8.79.73 1.06 1.6 2.25 2.74 2.21 1.1-.04 1.52-.71 2.85-.71 1.33 0 1.7.71 2.87.69 1.18-.02 1.93-1.08 2.66-2.14.84-1.23 1.18-2.42 1.2-2.48-.03-.01-2.3-.88-2.32-3.49zM14.2 6.15c.61-.74 1.02-1.76.9-2.78-.88.04-1.94.58-2.57 1.32-.56.65-1.06 1.69-.92 2.69.98.08 1.98-.5 2.59-1.23z" />
        </svg>
      )}
      {google && (
        <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
          <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
          <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
          <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
          <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
        </svg>
      )}
      {icon && (
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" className="shrink-0" aria-hidden>
          <path d={icon} stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      {swatch && <span className="h-2.5 w-2.5 rounded-full" style={{ background: swatch, boxShadow: '0 0 0 2px rgba(255,255,255,.7)' }} />}
      {selected && !noCheck && (
        <svg width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden>
          <path d="M2.5 6.2l2.3 2.3 4.7-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      {label}
    </button>
  );
});

/** A row of chips. `dx` nudges the whole row sideways (vw units). */
export function StageRow({ children, dx = 0 }: { children: ReactNode; dx?: number }) {
  return (
    <div data-a="chip" className="flex w-full flex-col items-center">
      <div className="max-w-full">
        <div className="flex max-w-full flex-wrap justify-center gap-2.5" style={{ transform: `translateX(${dx}vw)` }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ── Capsule ────────────────────────────────────────────────────────

export interface StageCapsuleProps {
  label?: string;
  value?: string;
  /** Renders an input bound to `value`. */
  editable?: boolean;
  onChange?: (v: string) => void;
  placeholder?: string;
  inputType?: string;
  autoComplete?: string;
  /** Lit: halo, glow, slow pulse. */
  focus?: boolean;
  accent?: string;
  locked?: boolean;
  chevron?: boolean;
  nova?: NovaState;
  tag?: string;
  toggle?: boolean;
  on?: boolean;
  onToggle?: () => void;
  /** 0–100 fill from the left. */
  progress?: number;
  /** Large numeric capsule (the goal number). */
  big?: boolean;
  dim?: boolean;
  strike?: boolean;
  maxWidth?: string;
  children?: ReactNode;
}

export function StageCapsule({
  label, value = '', editable, onChange, placeholder, inputType = 'text', autoComplete, focus, accent = '#E8B54A',
  locked, chevron, nova, tag, toggle, on, onToggle, progress, big, dim, strike, maxWidth = '580px', children,
}: StageCapsuleProps) {
  const icon = label ? iconFor(label) : '';
  const h = big ? 'clamp(96px, 9vw, 128px)' : 'clamp(58px, 4.5vw, 64px)';
  const px = big ? '28px' : 'clamp(18px, 1.7vw, 24px)';
  const fs = big ? 'clamp(52px, 5.3vw, 76px)' : 'clamp(16px, 1.2vw, 17px)';
  return (
    <div data-a="cap" className="flex w-full flex-col items-center">
      <div
        className="box-border w-full rounded-full p-[7px]"
        style={{
          maxWidth,
          background: focus ? `linear-gradient(135deg, ${accent}33, rgba(214,196,255,.16), ${accent}22)` : 'transparent',
          boxShadow: focus ? `0 0 0 1px rgba(232,220,255,.45), 0 0 60px ${accent}77, 0 0 120px rgba(214,150,255,.35)` : 'none',
          margin: focus ? '6px 0' : 0,
          animation: focus ? 'frGlowPulse 3.6s ease-in-out infinite' : 'none',
        }}
      >
        <div
          className="relative flex items-center gap-3.5 overflow-hidden rounded-full"
          style={{
            height: h,
            padding: `0 ${px}`,
            background: 'linear-gradient(180deg, rgba(120,104,190,.62), rgba(80,64,150,.58))',
            backdropFilter: 'blur(24px) saturate(160%)',
            WebkitBackdropFilter: 'blur(24px) saturate(160%)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,.34),inset 0 0 0 1px rgba(255,255,255,.16),inset 0 -12px 30px rgba(0,0,0,.18),0 20px 50px rgba(10,4,40,.45)',
          }}
        >
          {progress != null && (
            <div
              className="absolute bottom-0 left-0 top-0 transition-[width] duration-[400ms]"
              style={{ width: `${progress}%`, background: `linear-gradient(90deg,rgba(255,255,255,.04),${accent}88)` }}
            />
          )}
          {nova && (
            <span className="relative grid shrink-0" style={{ animation: 'frNovaBob 3.2s ease-in-out infinite' }}>
              <Nova state={nova} size={34} />
            </span>
          )}
          {label && (
            <span className="relative inline-flex shrink-0 items-center gap-2 text-[13px] font-medium text-[#F2EFEB]/65" style={{ minWidth: 'clamp(64px, 6vw, 92px)' }}>
              {icon && (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d={icon} stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
              {label}
            </span>
          )}
          {editable ? (
            <input
              type={inputType}
              value={value}
              onChange={(e) => onChange?.(e.target.value)}
              placeholder={placeholder}
              autoComplete={autoComplete}
              aria-label={label || placeholder}
              className="relative w-full min-w-0 flex-1 border-0 bg-transparent p-0 text-[#F2EFEB] outline-none placeholder:text-[#F2EFEB]/50"
              style={{ fontSize: fs, fontWeight: big ? 600 : 500, letterSpacing: big ? '-0.035em' : '-0.01em', textAlign: big ? 'center' : 'left' }}
            />
          ) : children ? (
            <div className="relative min-w-0 flex-1">{children}</div>
          ) : (
            <span
              className="relative min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap font-medium tracking-[-0.01em]"
              style={{ fontSize: fs, color: locked ? 'rgba(242,239,235,.72)' : dim ? 'rgba(242,239,235,.6)' : '#F2EFEB', textDecoration: strike ? 'line-through' : 'none' }}
            >
              {value}
            </span>
          )}
          {tag && <span className="relative shrink-0 rounded-full bg-white/[0.16] px-2.5 py-1.5 text-[12px] font-semibold text-[#F2EFEB]">{tag}</span>}
          {locked && (
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" className="relative shrink-0" aria-hidden>
              <rect x="3" y="7" width="10" height="7" rx="1.5" stroke="rgba(242,239,235,.7)" strokeWidth="1.3" />
              <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" stroke="rgba(242,239,235,.7)" strokeWidth="1.3" />
            </svg>
          )}
          {chevron && (
            <svg width="13" height="13" viewBox="0 0 12 12" fill="none" className="relative shrink-0" aria-hidden>
              <path d="M3 4.5l3 3 3-3" stroke="rgba(242,239,235,.8)" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          )}
          {toggle && (
            <button
              type="button"
              onClick={onToggle}
              aria-label="Toggle"
              aria-pressed={!!on}
              className="relative h-8 w-[52px] shrink-0 rounded-2xl border-0 transition-colors duration-200"
              style={{ background: on ? accent : 'rgba(255,255,255,.24)' }}
            >
              <span className="absolute top-[3px] h-[26px] w-[26px] rounded-full bg-white shadow-[0_2px_6px_rgba(0,0,0,.25)] transition-[left] duration-200" style={{ left: on ? '23px' : '3px' }} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function StageHint({ children }: { children: ReactNode }) {
  return (
    <div data-a="block" className="flex w-full flex-col items-center">
      <div className="px-3 py-0.5 text-center text-[14px] text-[#F2EFEB]/85" style={{ textWrap: 'balance', textShadow: '0 1px 14px rgba(0,0,0,.55)' }}>
        {children}
      </div>
    </div>
  );
}

/** Any custom block (form preview, site preview, planet…). */
export function StageBlock({ children, kind = 'block' }: { children: ReactNode; kind?: 'block' | 'cap' }) {
  return (
    <div data-a={kind} className="flex w-full flex-col items-center">
      {children}
    </div>
  );
}

// ── Text + CTA ─────────────────────────────────────────────────────

export interface StageTextProps {
  /** e.g. "02 / 05", "Optional", "Passport" — in the accent. */
  count?: string;
  /** e.g. "Contact" — muted, tracked. */
  label?: string;
  /** Tour cards show the label large instead. */
  tour?: boolean;
  icon?: string;
  accent: string;
  title: string;
  body?: ReactNode;
  children?: ReactNode;
}

export function StageText({ count, label, tour, icon, accent, title, body, children }: StageTextProps) {
  return (
    <div className="relative z-[2] mx-auto flex w-full max-w-[640px] flex-col items-center gap-2.5 text-center">
      <div data-a="text" className="flex min-h-[30px] flex-wrap items-center justify-center gap-2.5">
        <img src="/marketing/orbit-retro-logo-white.png" alt="Orbit" className="h-8 w-auto" draggable={false} />
        {tour && label && <span className="text-[20px] font-semibold tracking-[-0.015em]">{label}</span>}
        {icon && (
          <span className="grid h-7 w-7 place-items-center rounded-full" style={{ background: `${accent}22`, boxShadow: `inset 0 0 0 1px ${accent}66` }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d={icon} stroke={accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        )}
        {count && <span className="text-[12px] font-semibold uppercase tracking-[.18em]" style={{ color: accent }}>{count}</span>}
        {!tour && label && <span className="text-[12px] font-medium uppercase tracking-[.18em] text-[#F2EFEB]/70">{label}</span>}
      </div>
      <h1 data-a="text" className="m-0 font-bold leading-[1.1] tracking-[-0.025em]" style={{ fontSize: 'clamp(28px, 3.3vw, 50px)', textWrap: 'balance', textShadow: '0 2px 30px rgba(0,0,0,.5)' }}>
        {title}
      </h1>
      {body && (
        <p data-a="text" className="m-0 max-w-[540px] leading-[1.45] text-[#F2EFEB]/85" style={{ fontSize: 'clamp(16px, 1.3vw, 19px)', textWrap: 'pretty' }}>
          {body}
        </p>
      )}
      {children}
    </div>
  );
}

export interface StageCTAProps {
  primary: string;
  onPrimary: () => void;
  disabled?: boolean;
  loading?: boolean;
  skip?: string;
  onSkip?: () => void;
  note?: string;
  /** The button element, for motion that launches from it. */
  buttonRef?: React.RefObject<HTMLButtonElement>;
}

export function StageCTA({ primary, onPrimary, disabled, loading, skip, onSkip, note, buttonRef }: StageCTAProps) {
  return (
    <div data-a="cta" className="fr-cta z-[3] flex w-auto flex-col items-center gap-1 md:max-w-[640px]">
      <button
        ref={buttonRef}
        type="button"
        onClick={onPrimary}
        disabled={disabled || loading}
        className="relative box-border inline-flex h-14 w-full max-w-full items-center justify-center overflow-hidden whitespace-nowrap rounded-full border-0 px-8 text-[17px] font-semibold tracking-[-0.01em] text-white transition-[background-color,opacity] duration-500 md:w-[400px]"
        style={{
          background: MISSION_BLUE,
          boxShadow: '0 12px 34px rgba(47,107,232,.5)',
          animation: disabled ? 'none' : 'frBlueBreath 4s ease-in-out infinite',
          opacity: disabled ? 0.45 : 1,
        }}
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ background: 'linear-gradient(110deg,transparent 35%,rgba(255,255,255,.55) 50%,transparent 65%)', backgroundSize: '250% 100%', animation: 'frShimmer 5.5s ease-in-out infinite' }}
        />
        <span className="relative">{loading ? 'One moment…' : primary}</span>
      </button>
      {skip && (
        <button type="button" onClick={onSkip} className="h-10 border-0 bg-transparent px-3 text-[15px] text-[#F2EFEB]/70 hover:text-[#F2EFEB]">
          {skip}
        </button>
      )}
      {note && <div className="px-0 pb-0.5 pt-2 text-[13px] text-[#F2EFEB]/60">{note}</div>}
    </div>
  );
}

// ── Frame ──────────────────────────────────────────────────────────

export interface FirstRunFrameProps {
  palette: string;
  accent: string;
  /** Top-left back button. */
  onBack?: () => void;
  /** Tour dots: [index, total]. */
  dots?: [number, number];
  /** Top-right glass button. */
  right?: { label: string; onClick: () => void };
  stageMaxWidth?: string;
  stage: ReactNode;
  text: ReactNode;
  cta: ReactNode;
  rootRef?: React.RefObject<HTMLDivElement>;
  /** Something behind the wash (the photo wall on the account screens). */
  behind?: ReactNode;
}

export function FirstRunFrame({ palette, accent, onBack, dots, right, stageMaxWidth = '760px', stage, text, cta, rootRef, behind }: FirstRunFrameProps) {
  return (
    <div
      ref={rootRef}
      className="fr-motion relative grid min-h-[100svh] w-full overflow-clip text-[#F2EFEB]"
      style={{ background: '#0B0709', fontFamily: "-apple-system,BlinkMacSystemFont,'SF Pro Display','SF Pro Text','Helvetica Neue',sans-serif" }}
    >
      {behind}
      <FirstRunBackdrop palette={palette} />
      <div className="relative flex min-w-0 flex-col px-4 pt-[clamp(14px,2vw,28px)] md:px-[clamp(16px,2.6vw,40px)]">
        {/* top bar */}
        <div className="relative z-[4] flex min-h-[44px] items-center justify-between gap-3">
          <div className="flex items-center gap-3.5">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                aria-label="Back"
                className="grid h-11 w-11 place-items-center rounded-full border-0 text-[#1A1422] backdrop-blur-xl"
                style={{ background: 'rgba(222,214,244,.55)' }}
              >
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden>
                  <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
            {dots && (
              <div className="flex gap-1.5" aria-hidden>
                {Array.from({ length: dots[1] }, (_, i) => (
                  <span key={i} className="h-1.5 rounded-[3px] transition-[width] duration-200" style={{ width: i === dots[0] ? 22 : 6, background: i === dots[0] ? '#F2EFEB' : 'rgba(255,255,255,.35)' }} />
                ))}
              </div>
            )}
          </div>
          {right && (
            <button type="button" onClick={right.onClick} className="h-11 rounded-full border-0 bg-white/[0.12] px-[18px] text-[15px] font-medium text-[#F2EFEB] backdrop-blur-xl">
              {right.label}
            </button>
          )}
        </div>

        {/* stage */}
        <div className="relative flex flex-1 flex-col items-center justify-center py-[clamp(24px,3vw,44px)] pb-[clamp(20px,2.4vw,36px)]">
          <StageSparks accent={accent} />
          <StageOrb palette={palette} />
          <div className="relative flex w-full flex-col items-center gap-3" style={{ maxWidth: stageMaxWidth }}>
            {stage}
          </div>
        </div>

        {text}
        {cta}
      </div>
    </div>
  );
}
